import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Star, CheckCircle, Files } from '@phosphor-icons/react';
import type { TopicListItem } from '../../utils/library';

interface TopicCardProps {
  topic: TopicListItem;
  isRead?: boolean;
}

const CATEGORY_COLORS: Record<string, { badge: string }> = {
  condition: { badge: 'bg-pink-50 text-pink-700 dark:bg-pink-950 dark:text-pink-300' },
  treatment: { badge: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300' },
  diagnosis: { badge: 'bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300' },
  lifestyle: { badge: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300' },
  fertility: { badge: 'bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300' },
  'mental-health': { badge: 'bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300' },
  general: { badge: 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.ceil((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 1) return 'Today';
  if (diffDays <= 7) return `${diffDays} days ago`;
  if (diffDays <= 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export default function TopicCard({ topic, isRead }: TopicCardProps) {
  const colors = CATEGORY_COLORS[topic.category] || CATEGORY_COLORS.general;

  return (
    <Link
      to={`/research/topics/${topic.slug}`}
      className="block bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow p-5 group"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${colors.badge}`}>
            {topic.category.replace('-', ' ')}
          </span>
          {topic.recentlyUpdated && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300 font-medium flex items-center gap-0.5">
              <Star size={10} weight="fill" /> Updated
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
          {isRead && <CheckCircle size={14} weight="fill" className="text-green-500" />}
          <ArrowRight size={16} className="text-gray-400 group-hover:text-pink-500 transition-colors" />
        </div>
      </div>

      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1 group-hover:text-pink-600 dark:group-hover:text-pink-400 transition-colors">
        {topic.title}
      </h3>

      {topic.subtitle && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 italic">{topic.subtitle}</p>
      )}

      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
        {topic.patientSummary}
      </p>

      <div className="flex items-center gap-3 text-[11px] text-gray-400 dark:text-gray-500">
        <span className="flex items-center gap-1">
          <Clock size={12} /> {topic.readTimeMinutes} min read
        </span>
        {!!topic.sourceDocumentCount && (
          <span className="flex items-center gap-1">
            <Files size={12} /> {topic.sourceDocumentCount} sources
          </span>
        )}
        <span>{formatDate(topic.lastUpdated)}</span>
      </div>

      {topic.conditionTags.length > 0 && (
        <div className="flex gap-1 mt-2 flex-wrap">
          {topic.conditionTags.slice(0, 3).map(tag => (
            <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              {tag.replace(/-/g, ' ')}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}
